import * as Rx from 'rxjs';
import { map, take } from 'rxjs/operators';
export var validUsernameRegex = /^[a-z][a-z0-9]{2,15}$/i;
export var validEmailRegex = new RegExp("^(([^<>()\\[\\]\\.,;:\\s@\"]+(\\.[^<>()\\[\\]\\.,;:\\s@\"]+)*)|(\".+\"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$");
export var containsContactDetailsRegex = new RegExp([
    /([\w\-+!#%*=?^{}|~]+(?:\.[\w\-+!#%*=?^{}|~]+)*)/,
    /@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,10}(?:\.[a-z]{2})?)/i,
]
    .map(function (r) { return r.source; })
    .join(''), 'i');
/** Only use this with datepicker inputs */
export var dateFormat = function (errorText) { return function (control) {
    // ignore timestamps because it can be confusing UX.
    // null can somehow be a valid date so also ignore it here.
    if (typeof control.value === 'number' || control.value === null) {
        return { DATE_FORMAT_ERROR: errorText };
    }
    // control.value should already be a date thanks to Material
    var date = new Date(control.value);
    return Number.isNaN(date.getTime()) ? { DATE_FORMAT_ERROR: errorText } : null;
}; };
export var dateRangeFormat = function (errorText) { return function (control) {
    if (typeof control.value === 'number' || control.value === null) {
        return { DATE_RANGE_FORMAT_ERROR: errorText };
    }
    var _a = control.value, begin = _a.begin, end = _a.end;
    return Number.isNaN(begin.getTime()) ||
        Number.isNaN(end.getTime()) ||
        begin > end
        ? { DATE_RANGE_FORMAT_ERROR: errorText }
        : null;
}; };
/**
 * Only use this with datepicker inputs
 * Checks that the date is a current or future date
 */
export var currentOrFutureDate = function (errorText) { return function (control) {
    // Assuming that control.value is of a correct date format
    var date = new Date(control.value);
    var currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    return currentDate > date
        ? { CURRENT_OR_FUTURE_DATE_ERROR: errorText }
        : null;
}; };
/**
 * Only use this with datepicker inputs
 * Checks that the date is before a given date
 */
export var maxDate = function (endDate, errorText) { return function (control) {
    var date = new Date(control.value);
    return endDate < date.getTime() ? { MAX_DATE_ERROR: errorText } : null;
}; };
/**
 * Only use this with datepicker inputs
 * Checks that the date is after a given date
 */
export var minDate = function (startDate, errorText) { return function (control) {
    var date = new Date(control.value);
    return startDate > date.getTime() ? { MIN_DATE_ERROR: errorText } : null;
}; };
/**
 * This will pass validation if the value is undefined or null, etc.
 * If your control is required, please use the `required` validator.
 */
export var minLength = function (length, errorText) { return function (control) {
    return control.value !== undefined &&
        control.value !== null &&
        typeof control.value.length === 'number' &&
        control.value.length < length
        ? { MIN_LENGTH_ERROR: errorText }
        : null;
}; };
/**
 * This will pass validation if the value is undefined or null, etc.
 * If your control is required, please use the `required` validator.
 */
export var maxLength = function (length, errorText) { return function (control) {
    return control.value !== undefined &&
        control.value !== null &&
        typeof control.value.length === 'number' &&
        control.value.length > length
        ? { MAX_LENGTH_ERROR: errorText }
        : null;
}; };
export var required = function (errorText) { return function (control) {
    return control.value !== undefined &&
        control.value !== null &&
        !(Array.isArray(control.value) && control.value.length === 0) &&
        control.value.toString().trim() !== ''
        ? null
        : { REQUIRED_ERROR: errorText };
}; };
export var someRequired = function (errorText) { return function (control) {
    return hasControls(control) &&
        control.controls &&
        Object.values(control.controls).find(function (c) { return c.value; })
        ? null
        : { SOME_REQUIRED_ERROR: errorText };
}; };
export var requiredTruthy = function (errorText) { return function (control) {
    return !control.value ? { REQUIRED_TRUTHY_ERROR: errorText } : null;
}; };
export var minValue = function (value, errorText) { return function (control) {
    return control.value >= value ? null : { MIN_VALUE_ERROR: errorText };
}; };
export var maxValue = function (value, errorText) { return function (control) {
    return control.value <= value ? null : { MAX_VALUE_ERROR: errorText };
}; };
export var minValueExclusive = function (value, errorText) { return function (control) {
    return control.value > value ? null : { MIN_VALUE_EXCLUSIVE_ERROR: errorText };
}; };
export var maxValueExclusive = function (value, errorText) { return function (control) {
    return control.value < value ? null : { MAX_VALUE_EXCLUSIVE_ERROR: errorText };
}; };
export var wholeNumber = function (errorText) { return function (control) {
    var error = { WHOLE_NUMBER_ERROR: errorText };
    if (!['string', 'number'].includes(typeof control.value)) {
        return error;
    }
    var wholeNumberRegex = new RegExp(/^[-+]?\d+\.?0*$/);
    return wholeNumberRegex.test(control.value.toString()) ? null : error;
}; };
export var pattern = function (regExp, errorText) { return function (control) {
    return regExp.test(control.value) ? null : { PATTERN_ERROR: errorText };
}; };
export var notPattern = function (regExp, errorText) { return function (control) {
    return !regExp || !regExp.test(control.value) ? null : { PATTERN_ERROR: errorText };
}; };
// This is for multiple regex patterns with OR statements between them
export var somePatterns = function (regexArray, errorText) { return function (control) {
    return regexArray.some(function (regex) { return regex.test(control.value); })
        ? null
        : { PATTERNS_ERROR: errorText };
}; };
/**
 * This is to make sure that the user doesn't upload a file with the same name
 * since we use the filename as an identifier for contest files. :'(
 *
 * Also, note that the control in filenamesUnique validator is only compared to
 * a static list of files. In order to compare against a running  list of files,
 * use filenamesUniqueAsync instead.
 */
export var filenamesUnique = function (uploadedFiles, errorText) { return function (control) {
    return control.value &&
        control.value.some(function (newFile) {
            return uploadedFiles.find(function (uploadedFile) { return uploadedFile.name === newFile.name; });
        })
        ? { FILENAMES_UNIQUE_ERROR: errorText }
        : null;
}; };
/**
 * File upload validators. Note that the controls for filesUnique, maxFileSize,
 * and minFileSize expect an array of File objects, not a single file.
 *
 * Also, note that the control in filesUnique validator is only compared to a
 * static list of files. In order to compare against a running  list of files,
 * use filesUniqueAsync instead.
 */
export var filesUnique = function (uploadedFiles, errorText) { return function (control) {
    return control.value &&
        control.value.some(function (newFile) {
            return uploadedFiles.find(function (uploadedFile) {
                return uploadedFile.name === newFile.name &&
                    uploadedFile.size === newFile.size;
            });
        })
        ? { FILES_UNIQUE_ERROR: errorText }
        : null;
}; };
/**
 * This expects an array of File objects.
 * NOTE: This validates the file's MIME type rather than extenstion.
 */
export var allowedFileTypes = function (fileTypes, errorText) { return function (control) {
    return control.value
        ? control.value.some(function (file) { return !fileTypes.includes(file.type.split('/')[1]); })
            ? { ALLOWED_FILE_TYPE_ERROR: errorText }
            : null
        : null;
}; };
// This expects an array of File objects.
export var maxFileSize = function (size, errorText) { return function (control) {
    return control.value
        ? control.value.some(function (file) { return size < file.size; })
            ? { MAX_FILE_SIZE_ERROR: errorText }
            : null
        : null;
}; };
// This expects an array of File objects.
export var minFileSize = function (size, errorText) { return function (control) {
    return control.value
        ? control.value.some(function (file) { return size > file.size; })
            ? { MIN_FILE_SIZE_ERROR: errorText }
            : null
        : null;
}; };
/**
 * This validator errors if the subject text matches at least one term from ALL of the blacklists given.
 * For example, if two blacklists are given, but the text only matches a term in one of them, the validation passes.
 * However, if the text matches a term in both lists, the validation errors.
 * The terms of each blacklist can either be a string literal ('foo') or a regular expression ('foo.*bar').
 */
export var noBlacklistTerms = function (blacklists, errorText, errorKey) {
    if (errorKey === void 0) { errorKey = 'BLACKLISTS_ERROR'; }
    return function (control) {
        var _a;
        if (blacklists.length === 0) {
            return null;
        }
        var subjectText = control.value.toLowerCase();
        var hasBlacklistTerm = blacklists
            .map(function (blacklist) {
            return blacklist.some(function (term) {
                var matches = subjectText.match(term);
                return matches && matches.length > 0;
            });
        })
            .every(function (hasTerm) { return hasTerm; });
        return hasBlacklistTerm ? (_a = {}, _a[errorKey] = errorText, _a) : null;
    };
};
export var notEqualOtherControl = function (otherControl, errorText) { return function (control) {
    return control.value
        ? control.value === otherControl.value
            ? { NOT_EQUAL_OTHER_CONTROL_ERROR: errorText }
            : null
        : null;
}; };
/**
 * Recursively validates the control and all its children
 * @param control AbstractControl
 */
export function dirtyAndValidate(control) {
    control.markAsDirty();
    control.updateValueAndValidity();
    if (hasControls(control) && control.controls) {
        Object.values(control.controls).forEach(function (c) {
            if (control.enabled) {
                dirtyAndValidate(c);
            }
        });
    }
}
/**
 * This validator checks if a form group has the minimum required number of form controls that are TRUE.
 * Example: Given a list of checkbox each having its own form control and belonging to one form group,
 * we can validate if at least one check box has been ticked/checked using this validator.
 */
export var minNumOfTrue = function (minTrue, errorText) {
    if (minTrue === void 0) { minTrue = 1; }
    return function (control) {
        var trueControls = [];
        if (hasControls(control) && control.controls) {
            trueControls = Object.values(control.controls).filter(function (c) { return c.value === true; });
        }
        return trueControls.length < minTrue
            ? { MIN_NUM_OF_TRUE_ERROR: errorText }
            : null;
    };
};
function hasControls(c) {
    return 'controls' in c;
}
/**
 * A function that takes in another validator generator,
 * and creates an async validator generator called in the same way.
 * Use this to allow a validator with an extra argument to be called as an AsyncValidator
 *
 * Note: observables passed into this must complete!
 * Treat these async validators like toPromise()
 */
export function asyncValidatorOf(baseValidator) {
    return function (value$, errorText$) { return function (control) {
        var _errorTextSubject$ = Rx.isObservable(errorText$)
            ? errorText$
            : Rx.of(errorText$);
        return Rx.combineLatest([value$, _errorTextSubject$]).pipe(take(1), map(function (_a) {
            var value = _a[0], errorTextString = _a[1];
            return baseValidator(value, errorTextString)(control);
        }));
    }; };
}
/**
 * Async version of filenamesUnique.
 * Note: observable argument must complete, similar to toPromise()
 */
export var filenamesUniqueAsync = asyncValidatorOf(filenamesUnique);
/**
 * Async version of filesUnique.
 * Note: observable argument must complete, similar to toPromise()
 */
export var filesUniqueAsync = asyncValidatorOf(filesUnique);
/**
 * Async version of minLength.
 * Note: observable argument must complete, similar to toPromise()
 */
export var minLengthAsync = asyncValidatorOf(minLength);
/**
 * Async version of maxLength.
 * Note: observable argument must complete, similar to toPromise()
 */
export var maxLengthAsync = asyncValidatorOf(maxLength);
/**
 * Async version of minValue.
 * Note: observable argument must complete, similar to toPromise()
 */
export var minValueAsync = asyncValidatorOf(minValue);
/**
 * Async version of maxValue.
 * Note: observable argument must complete, similar to toPromise()
 */
export var maxValueAsync = asyncValidatorOf(maxValue);
/**
 * Async version of minValueExclusive.
 * Note: observable argument must complete, similar to toPromise()
 */
export var minValueExclusiveAsync = asyncValidatorOf(minValueExclusive);
/**
 * Async version of maxValueExclusive.
 * Note: observable argument must complete, similar to toPromise()
 */
export var maxValueExclusiveAsync = asyncValidatorOf(maxValueExclusive);
/**
 * Async version of pattern.
 * Note: observable argument must complete, similar to toPromise()
 */
export var patternAsync = asyncValidatorOf(pattern);
/**
 * Async version of notPatter.
 * Note: observable argument must complete, similar to toPromise()
 */
export var notPatternAsync = asyncValidatorOf(notPattern);
/**
 * Async version of somePatterns.
 * Note: observable argument must complete, similar to toPromise()
 */
export var somePatternsAsync = asyncValidatorOf(somePatterns);
/**
 * Async version of minDate.
 * Note: observable argument must complete, similar to toPromise()
 */
export var minDateAsync = asyncValidatorOf(minDate);
/**
 * Async version of maxDate.
 * Note: observable argument must complete, similar to toPromise()
 */
export var maxDateAsync = asyncValidatorOf(maxDate);
